import React, { useState } from 'react';
import { X, Instagram, Youtube, Music, Target, ArrowRight, Users, Zap } from 'lucide-react';
import { useSocialMediaStore } from '../../stores/socialMediaStore';
import { useUserStore } from '../../stores/userStore';
import { TRANSITIONS } from '../../utils/animations';
import toast from 'react-hot-toast';

interface OnboardingModalProps {
  onComplete: () => void;
}

type Platform = 'instagram' | 'youtube' | 'tiktok';
type Step = 'platform' | 'username' | 'followers' | 'goals';

export default function OnboardingModal({ onComplete }: OnboardingModalProps) {
  const [step, setStep] = useState<Step>('platform');
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [username, setUsername] = useState('');
  const [followers, setFollowers] = useState('');
  const [goal, setGoal] = useState('');
  const [targetFollowers, setTargetFollowers] = useState('');
  const [targetViews, setTargetViews] = useState('');
  
  const { connectAccount } = useSocialMediaStore();
  const { setOnboardingComplete, setUserGoals } = useUserStore();

  const handlePlatformSelect = (platform: Platform) => {
    setSelectedPlatform(platform);
    setStep('username');
  };

  const handleUsernameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Por favor, informe seu nome de usuário');
      return;
    }
    setStep('followers');
  };

  const handleFollowersSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!followers.trim()) {
      toast.error('Por favor, informe seu número de seguidores');
      return;
    }
    setStep('goals');
  };

  const handleGoalsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!goal.trim() || (!targetFollowers.trim() && !targetViews.trim())) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    // Connect the account with the provided information
    if (selectedPlatform) {
      connectAccount(selectedPlatform);
    }

    // Save user goals
    setUserGoals({
      platform: selectedPlatform || 'instagram',
      username,
      followers: parseInt(followers) || 0,
      goal,
      targetFollowers: parseInt(targetFollowers) || 0,
      targetViews: parseInt(targetViews) || 0
    });

    // Mark onboarding as complete
    setOnboardingComplete(true);
    
    toast.success('Perfil configurado com sucesso!');
    onComplete();
  };

  const formatNumber = (value: string): string => {
    // Remove non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    // Format with thousands separators
    if (numericValue) {
      return new Intl.NumberFormat('pt-BR').format(parseInt(numericValue));
    }
    
    return '';
  };

  const renderStepIndicator = () => (
    <div className="flex justify-between mb-8">
      {(['platform', 'username', 'followers', 'goals'] as Step[]).map((s, index) => (
        <div key={s} className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            step === s 
              ? 'bg-indigo-600 text-white' 
              : index < ['platform', 'username', 'followers', 'goals'].indexOf(step) + 1
                ? 'bg-indigo-100 text-indigo-600'
                : 'bg-gray-100 text-gray-400'
          }`}>
            {index + 1}
          </div>
          <span className={`text-xs mt-1 ${
            step === s 
              ? 'text-indigo-600' 
              : 'text-gray-500'
          }`}>
            {s === 'platform' && 'Plataforma'}
            {s === 'username' && 'Usuário'}
            {s === 'followers' && 'Seguidores'}
            {s === 'goals' && 'Objetivos'}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-xl max-w-md w-full p-6 ${TRANSITIONS.DEFAULT}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Bem-vindo à Kontty!
          </h2>
          <button 
            onClick={onComplete} 
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {renderStepIndicator()}

        {step === 'platform' && (
          <div className={`space-y-6 ${TRANSITIONS.FADE_IN}`}>
            <p className="text-gray-600">
              Para começar, selecione sua principal plataforma de conteúdo:
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => handlePlatformSelect('instagram')}
                className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg border border-pink-200 hover:shadow-md transition-all flex flex-col items-center"
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-2">
                  <Instagram className="h-6 w-6 text-pink-600" />
                </div>
                <span className="text-sm font-medium">Instagram</span>
              </button>
              
              <button
                onClick={() => handlePlatformSelect('youtube')}
                className="p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200 hover:shadow-md transition-all flex flex-col items-center"
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-2">
                  <Youtube className="h-6 w-6 text-red-600" />
                </div>
                <span className="text-sm font-medium">YouTube</span>
              </button>
              
              <button
                onClick={() => handlePlatformSelect('tiktok')}
                className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg border border-cyan-200 hover:shadow-md transition-all flex flex-col items-center"
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-2">
                  <Music className="h-6 w-6 text-black" />
                </div>
                <span className="text-sm font-medium">TikTok</span>
              </button>
            </div>
            
            <p className="text-sm text-gray-500 text-center">
              Você poderá adicionar outras plataformas depois nas configurações.
            </p>
          </div>
        )}

        {step === 'username' && (
          <form onSubmit={handleUsernameSubmit} className={`space-y-6 ${TRANSITIONS.FADE_IN}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qual é o seu nome de usuário no {selectedPlatform === 'instagram' ? 'Instagram' : selectedPlatform === 'youtube' ? 'YouTube' : 'TikTok'}?
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  placeholder="seu_usuario"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Isso nos ajudará a personalizar suas recomendações.
              </p>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
              >
                Continuar
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </form>
        )}

        {step === 'followers' && (
          <form onSubmit={handleFollowersSubmit} className={`space-y-6 ${TRANSITIONS.FADE_IN}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantos seguidores você tem atualmente?
              </label>
              <input
                type="text"
                value={followers}
                onChange={(e) => setFollowers(formatNumber(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                placeholder="1.000"
              />
              <p className="mt-1 text-xs text-gray-500">
                Esta informação nos ajuda a definir estratégias adequadas para seu nível.
              </p>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
              >
                Continuar
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </form>
        )}

        {step === 'goals' && (
          <form onSubmit={handleGoalsSubmit} className={`space-y-6 ${TRANSITIONS.FADE_IN}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qual é o seu principal objetivo com seu conteúdo?
              </label>
              <select
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              >
                <option value="">Selecione um objetivo</option>
                <option value="crescer">Crescer minha audiência</option>
                <option value="engajar">Aumentar engajamento</option>
                <option value="monetizar">Monetizar meu conteúdo</option>
                <option value="marca">Construir minha marca pessoal</option>
                <option value="vendas">Aumentar vendas/conversões</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meta de seguidores
                </label>
                <input
                  type="text"
                  value={targetFollowers}
                  onChange={(e) => setTargetFollowers(formatNumber(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  placeholder="10.000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meta de visualizações
                </label>
                <input
                  type="text"
                  value={targetViews}
                  onChange={(e) => setTargetViews(formatNumber(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  placeholder="50.000"
                />
              </div>
            </div>
            
            <p className="text-sm text-gray-500">
              Estas metas nos ajudarão a personalizar estratégias para alcançar seus objetivos.
            </p>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
              >
                Concluir
                <Zap className="ml-2 h-4 w-4" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}